import { match } from "ts-pattern";

import type {
  EMHAParticipantUnbranded,
  ShareableIncidentDataV1Response,
} from "@thalamos/common";
import { OfficerRequestStatusSchema } from "@thalamos/common";
import type {
  ExtendedFormDraft,
  FormContextData,
  MhaStatusState,
} from "@thalamos/aspire-common";
import { COMPLETED_FORM_STATUSES, MhaStatus } from "@thalamos/aspire-common";

/**
 * Finds the most relevant eMHA participant for POS config lookups.
 *
 * First tries to find a participant whose userId AND teamId both match the
 * current session context. This is important because a user can be a member
 * of multiple MHA teams, each potentially associated with a different POS.
 * If no exact user+team match is found, falls back to the most
 * recently-accessed participant in the same team — this covers the case where
 * a teammate confirmed the POS and other team members are assumed to be at the
 * same place of safety.
 *
 * @param participants - The list of eMHA participants from shareable incident data
 * @param userId - The current user's ID, or undefined if not available
 * @param teamId - The current user's team ID, or undefined if not available
 * @returns The resolved participant, or undefined if none can be found
 */
export function resolveEmhaParticipant(
  participants: EMHAParticipantUnbranded[],
  userId: string | undefined,
  teamId: string | undefined,
): EMHAParticipantUnbranded | undefined {
  // Match on both userId and teamId: a user can belong to multiple MHA teams,
  // each with its own POS. An entry from a different team must not bleed across.
  const byUserAndTeam = participants.find(
    (p) => p.userId === userId && p.teamId === teamId,
  );
  if (byUserAndTeam) return byUserAndTeam;

  if (!teamId) return undefined;

  return participants
    .filter((p) => p.teamId === teamId)
    .sort(
      (a, b) =>
        new Date(b.accessedAt).getTime() - new Date(a.accessedAt).getTime(),
    )[0];
}

/**
 * Builds the detention-start pre-fill for a new S136 extension form draft.
 *
 * When the patient is detained under Section 136, their MHA status carries the
 * original detention start time (`startDateTime`, set at acceptance and left
 * unchanged through extensions). We pre-fill the extension form's
 * `detentionStartDateTime` from it so the value stays consistent with the
 * recorded 136 status. The field remains editable.
 *
 * The guard on `Section136` matters for dual-status patients (an active 136
 * alongside another detention), where the combined status passed in may be the
 * non-136 one — in that case we return an empty object and leave the field
 * blank for manual entry, as before.
 *
 * @param mhaStatus - The patient's current MHA status, or undefined
 * @returns `{ detentionStartDateTime }` when on a 136 with a start time, else `{}`
 */
export function buildDetentionStartPrefill(
  mhaStatus: MhaStatusState | undefined,
): { detentionStartDateTime?: string } {
  if (mhaStatus?.status === MhaStatus.Section136 && mhaStatus.startDateTime) {
    return { detentionStartDateTime: mhaStatus.startDateTime };
  }
  return {};
}

/**
 * The possible police incident types in the aspire-frontend police incident flow.
 */
export type PoliceIncidentType = "s136" | "mca";

/**
 * The incident translation context values used with i18next's context feature.
 * These are used as suffixes for translation keys to provide incident-specific
 * terminology (e.g., "Section 136" for s136 vs "Mental Capacity Act" for MCA).
 */
export const IncidentTranslationContext = {
  s136: "s136",
  mca: "mca",
} as const;

export type IncidentTranslationContextValue =
  (typeof IncidentTranslationContext)[keyof typeof IncidentTranslationContext];

/**
 * Maps police incident types to their form template IDs.
 * Adding a new entry here and to `PoliceIncidentType` will trigger
 * a compile-time error in the exhaustive match within `policeIncidentLogic`,
 * ensuring the new incident type is properly handled.
 */
export const PoliceFormTemplateId = {
  s136: "mha-136",
  mca: "mca-police",
} as const;

type PoliceFormTemplateIdValue =
  (typeof PoliceFormTemplateId)[keyof typeof PoliceFormTemplateId];

/**
 * Gets the appropriate i18next translation context based on the police incident type.
 *
 * Uses ts-pattern exhaustive matching so that adding a new incident type
 * to `PoliceIncidentType` will produce a compile-time error here,
 * forcing the developer to add the corresponding translation context.
 *
 * @param incidentType - The police incident type
 * @returns The incident translation context value ("s136" or "mca")
 *
 * @example
 * const context = getIncidentTranslationContext(incidentType);
 * const label = t("pages.policeIncidentFormPage.title", { context });
 * // For s136: uses key "title_s136"
 * // For MCA: uses key "title_mca"
 */
export const getIncidentTranslationContext = (
  incidentType: PoliceIncidentType,
): IncidentTranslationContextValue =>
  match(incidentType)
    .with("s136", () => IncidentTranslationContext.s136)
    .with("mca", () => IncidentTranslationContext.mca)
    .exhaustive();

export function policeIncidentLogic(
  formContext: FormContextData,
  shareableIncidentData: ShareableIncidentDataV1Response | null | undefined,
  userId?: string,
) {
  const myWorkItem = formContext.activeTeamworkWorkItem;

  const isUserFullMhaAdmin = myWorkItem?.team.type === "mha";
  const isUserGuest = myWorkItem?.team.type === "guest";
  const isUserMhaAdmin =
    isUserFullMhaAdmin ||
    (isUserGuest && myWorkItem.requestType === "finalise");

  const policeForm = formContext.forms.find(
    (f) =>
      f.formTemplate.id === PoliceFormTemplateId.s136 ||
      f.formTemplate.id === PoliceFormTemplateId.mca,
  );

  if (!policeForm) {
    throw new Error(
      `No police form found in form context ${formContext.id}. ` +
        `Expected a form with template ID "${PoliceFormTemplateId.s136}" or "${PoliceFormTemplateId.mca}".`,
    );
  }

  const incidentType: PoliceIncidentType = match(
    policeForm.formTemplate.id as PoliceFormTemplateIdValue,
  )
    .with(PoliceFormTemplateId.s136, () => "s136" as const)
    .with(PoliceFormTemplateId.mca, () => "mca" as const)
    .exhaustive();

  const monitoringForms = myWorkItem
    ? myWorkItem.forms.filter((f) => f.formTemplate.id === "mha-136-monitoring")
    : [];

  // Legacy singular reference — first monitoring form found on the work item.
  // Used only by legacy fallback UI paths (when no per-POS outcome form cards exist).
  const monitoringForm = monitoringForms[0] ?? null;

  // "Any" semantics: at least one monitoring form is in the given state.
  // Used for legacy fallback UI paths.
  const monitoringFormInProgress = monitoringForms.some(
    (f) => f.status === "in-progress",
  );

  // "All" semantics: every monitoring form is completed.
  // This is the correct semantic for incident-level status checks —
  // the incident should only be considered "done with monitoring" when ALL
  // per-POS outcome forms have been completed.
  const allMonitoringFormsCompleted =
    monitoringForms.length > 0 &&
    monitoringForms.every((f) => ["finalised", "complete"].includes(f.status));

  // "Any" semantics: at least one monitoring form has been completed.
  // Used for legacy fallback display (e.g. showing a completed card when
  // no per-POS outcome form cards exist).
  const anyMonitoringFormCompleted = monitoringForms.some((f) =>
    ["finalised", "complete"].includes(f.status),
  );

  // Backward-compatible: reflects whether any single form is completed.
  // Legacy consumers that check `monitoringFormCompleted` in the UI now get
  // "any completed" semantics, which keeps legacy fallback cards accurate.
  const monitoringFormCompleted = anyMonitoringFormCompleted;

  // All completed monitoring form IDs — used for download-all / upload-all bundles
  const completedMonitoringFormIds: string[] = monitoringForms
    .filter((f) => ["finalised", "complete"].includes(f.status))
    .map((f) => f.id);

  // S136 Extension form logic
  // Look at both the current user's work item AND all forms across the form context.
  // This ensures we detect extension forms started/completed by other participants.
  //
  // Extension forms are personal per user AND per team — if a user is a member
  // of two teams that both participate in the same incident, each team has its
  // own independent extension form.
  const currentTeamId = myWorkItem?.teamId ?? undefined;

  // Work-item form: scoped to the current team's work item.
  // Filter by ownerTeamId so we don't pick up another team's extension form.
  const s136ExtensionForm =
    myWorkItem && currentTeamId
      ? (myWorkItem.forms.find(
          (f) =>
            f.formTemplate.id === "s136-extension" &&
            f.status !== "deleted" &&
            f.ownerTeamId === currentTeamId,
        ) ?? null)
      : null;

  // Context-wide lookup: any s136-extension form across all work items
  const anyS136ExtensionForm =
    formContext.forms.find(
      (f) => f.formTemplate.id === "s136-extension" && f.status !== "deleted",
    ) ?? null;

  // User+team-scoped lookup: extension form owned by the current user
  // in their current team context.
  const myS136ExtensionContextForm =
    userId && currentTeamId
      ? (formContext.forms.find(
          (f) =>
            f.formTemplate.id === "s136-extension" &&
            f.status !== "deleted" &&
            f.ownerUserId === userId &&
            f.ownerTeamId === currentTeamId,
        ) ?? null)
      : null;

  // The "effective" extension form for this user+team: prefer the scoped
  // lookup (by ownerUserId + ownerTeamId) when available; otherwise fall
  // back to the work-item-derived form which is gated by formDraftId.
  // When userId IS provided but no form matches, we intentionally leave
  // myEffectiveExtensionForm as null — the user doesn't have their own form
  // in this team context.
  const myEffectiveExtensionForm = userId
    ? myS136ExtensionContextForm
    : (myS136ExtensionContextForm ?? s136ExtensionForm);

  // Find the user's personal in-progress draft from formContext.forms.allDrafts.
  // This works regardless of whether the user has claimed the outcome form /
  // work item, because allDrafts is populated from the form context — not
  // from the work item.
  // Use the context form (not the work-item form) because only
  // FormContextMiniForm carries allDrafts.
  // Also scope by ownerTeamId to avoid picking up drafts from another team.
  const myExtensionDraft =
    userId && currentTeamId
      ? (myS136ExtensionContextForm?.allDrafts?.find(
          (d: ExtendedFormDraft) =>
            d.ownerUserId === userId &&
            d.ownerTeamId === currentTeamId &&
            d.status === "in-progress",
        ) ?? null)
      : null;

  // User's own extension form status — check:
  //   1. allDrafts-derived draft (works without claiming)
  //   2. user's own form from formContext.forms (by ownerUserId + ownerTeamId)
  // formDraftId from the work item is NOT used here because it reflects the
  // work item's assignedUserId — not the viewing user — so it leaks another
  // user's draft to teammates on the same work item.
  const myS136ExtensionFormInProgress =
    myExtensionDraft !== null ||
    myEffectiveExtensionForm?.status === "in-progress";
  const myS136ExtensionFormCompleted =
    (userId &&
      currentTeamId &&
      myS136ExtensionContextForm?.allDrafts?.some(
        (d: ExtendedFormDraft) =>
          d.ownerUserId === userId &&
          d.ownerTeamId === currentTeamId &&
          d.status === "completed",
      )) ||
    ["finalised", "complete"].includes(myEffectiveExtensionForm?.status ?? "");

  // Context-wide status checks (covers forms started by other participants)
  // Used for interaction/capability checks after the step is already visible
  const s136ExtensionFormInProgress =
    (anyS136ExtensionForm?.status ?? s136ExtensionForm?.status) ===
    "in-progress";
  const s136ExtensionFormCompleted = ["finalised", "complete"].includes(
    anyS136ExtensionForm?.status ?? s136ExtensionForm?.status ?? "",
  );

  // Detect extension forms that were force-finalised from an in-progress state
  // without ever being signed. These should not offer View/Download PDF actions
  // because no PDF was generated.
  const effectiveExtensionForm = anyS136ExtensionForm ?? s136ExtensionForm;
  const s136ExtensionFormWasNeverCompleted =
    effectiveExtensionForm?.status === "finalised" &&
    effectiveExtensionForm.preFinalisedFormStatus === "in-progress";

  // Draft ID: use allDrafts-derived draft only — this is the user+team-scoped
  // source. The work item's formDraftId is intentionally NOT used because it
  // is scoped to the work item's assignedUserId, not the viewing user.
  const s136ExtensionFormDraftId: string | false =
    myExtensionDraft?.id ?? false;

  // The form ID from either source (needed for other accredited users to create a new draft)
  const s136ExtensionFormId =
    s136ExtensionForm?.id ?? anyS136ExtensionForm?.id ?? null;

  const isFinalised =
    policeForm?.status === "finalised" || formContext.status === "closed";

  const canMonitoringFormBeWorkedOn =
    !allMonitoringFormsCompleted && !isFinalised && myWorkItem;

  // S136 Extension can be worked on regardless of work item claim status.
  // Users should be able to start/continue their personal extension form
  // drafts even if they haven't claimed the outcome form.
  // Once any team has signed the extension form, no other team may start
  // or continue one — only one signed extension form per incident.
  const canS136ExtensionFormBeWorkedOn =
    !myS136ExtensionFormCompleted &&
    !s136ExtensionFormCompleted &&
    !isFinalised;

  const isIncidentInProgress =
    shareableIncidentData &&
    !allMonitoringFormsCompleted &&
    match(shareableIncidentData.incidentStatus)
      .with(
        "arrived-at-place-of-safety",
        "awaiting-arrival",
        "form-shared",
        "in-progress",
        "nhs-form-accepted",
        "police-presence-requested",
        "police-presence-not-requested",
        () => true,
      )
      .with("ended", "unknown", () => false)
      .exhaustive();

  // Legacy: draft ID for the first in-progress monitoring form.
  // In the new per-POS flow, draft IDs come from OutcomeFormSection instead.
  const inProgressMonitoringForm = monitoringForms.find(
    (f) => f.status === "in-progress",
  );
  const userDraftId: string | false =
    inProgressMonitoringForm?.formDraftId ?? false;

  const needsRequestPolicePresenceResponse =
    shareableIncidentData?.officerRequest.status ===
      OfficerRequestStatusSchema.enum.decisionNeeded &&
    isIncidentInProgress &&
    !anyMonitoringFormCompleted;

  const isVisionIncidentParticipant =
    shareableIncidentData?.emhaParticipants.some(
      (p) => p.teamId === formContext.activeTeamworkWorkItem?.teamId,
    ) ?? false;

  // Not yet finalised and user is either a full MHA admin or a guest user
  // invited in a scrutiny capacity
  const canFinaliseIncidentContext =
    policeForm?.status === "complete" && isUserMhaAdmin;

  const policeFormCompleted = COMPLETED_FORM_STATUSES.includes(
    policeForm?.status,
  );

  // MHA admins can upload completed forms to external systems (e.g. Rio)
  const mayUploadFormsToExternalSystem = isUserMhaAdmin;

  return {
    incidentType,
    monitoringForm,
    monitoringForms,
    monitoringFormInProgress,
    monitoringFormCompleted,
    allMonitoringFormsCompleted,
    anyMonitoringFormCompleted,
    completedMonitoringFormIds,
    policeFormCompleted,
    userDraftId,
    policeForm: policeForm,
    needsRequestPolicePresenceResponse,
    isVisionIncidentParticipant,
    isIncidentInProgress,
    isUserMhaAdmin,
    canFinaliseIncidentContext,
    canMonitoringFormBeWorkedOn,
    isFinalised,
    mayUploadFormsToExternalSystem,
    // S136 Extension form
    s136ExtensionForm,
    myS136ExtensionFormInProgress,
    myS136ExtensionFormCompleted,
    s136ExtensionFormInProgress,
    s136ExtensionFormCompleted,
    s136ExtensionFormDraftId,
    s136ExtensionFormId,
    canS136ExtensionFormBeWorkedOn,
    s136ExtensionFormWasNeverCompleted,
  };
}
